/**
 * Work order service line operations (assignment, splits, status, pricing, approval, hierarchy).
 *
 * Extracted verbatim from workOrdersService.ts — functions are invoked with
 * `this` bound to the WorkOrdersService instance (WorkOrdersCtx).
 */
import { approveWorkOrder } from './lifecycle';
import { extractItems } from '../../utils/apiHelpers';
/**
 * Assign user to a specific service within a work order
 * POST /work-orders/{id}/services/{wos_id}/assign-user
 */
export async function assignUserToService(workOrderId, wosId, userId) {
    try {
        const response = await this.client.post(`/work-orders/${workOrderId}/services/${wosId}/assign-user`, { user_id: userId });
        if (!response.success) {
            throw new Error(response.error || 'Failed to assign user to service');
        }
    }
    catch (error) {
        console.error('[WorkOrdersService] Assign user to service error:', error);
        throw error;
    }
}
/**
 * Unassign user from a specific service within a work order
 * POST /work-orders/{id}/services/{wos_id}/assign-user with null user_id
 */
export async function unassignUserFromService(workOrderId, wosId) {
    try {
        const response = await this.client.post(`/work-orders/${workOrderId}/services/${wosId}/assign-user`, { user_id: null });
        if (!response.success) {
            throw new Error(response.error || 'Failed to unassign user from service');
        }
    }
    catch (error) {
        console.error('[WorkOrdersService] Unassign user from service error:', error);
        throw error;
    }
}
/**
 * Assign user to all services in a department.
 * POST /work-orders/{id}/departments/{department_id}/assign-user
 *
 * When `splits` is provided, services in sections with supports_splits=true
 * (and on accounts with allow_service_splits=true) get a 50/50 split;
 * services where splits aren't allowed gracefully fall back to primary-only
 * assignment. The backend also clears any pre-existing splits when
 * assigning a single user, so reassigning a previously-split department
 * doesn't leave orphaned splits behind.
 *
 * Returns the full bulk-assign outcome so callers can surface partial
 * success: `services_assigned` plus a normalized `services_skipped` array
 * (empty on full success — older backends that omit the field, or omit the
 * body entirely, normalize to `services_skipped: []`).
 */
export async function assignUserToDepartmentServices(workOrderId, departmentId, userId, splits) {
    try {
        const body = { user_id: userId };
        if (splits && splits.length > 0) {
            body.splits = splits;
        }
        const response = await this.client.post(`/work-orders/${workOrderId}/departments/${departmentId}/assign-user`, body);
        if (!response.success) {
            throw new Error(response.error || 'Failed to assign user to department services');
        }
        const data = (response.data ?? {});
        return {
            message: typeof data.message === 'string' ? data.message : undefined,
            services_assigned: typeof data.services_assigned === 'number' ? data.services_assigned : 0,
            services_skipped: Array.isArray(data.services_skipped) ? data.services_skipped : [],
            // "splits_not_applied" arrives here: the services WERE assigned, but to
            // the primary tech alone. Dropping it made a refused split look done.
            services_notices: Array.isArray(data.services_notices) ? data.services_notices : [],
        };
    }
    catch (error) {
        console.error('[WorkOrdersService] Assign user to department services error:', error);
        throw error;
    }
}
/**
 * Per-service revenue splits — three-call CRUD surface mirroring
 * /work-orders/{id}/services/{wos_id}/splits.
 *
 * - getServiceSplits: returns the persisted set in created_at order;
 *   empty array when no splits exist
 * - putServiceSplits: replaces the set wholesale (no partial updates).
 *   The first splittee becomes the service's primary AssignedUserID.
 *   Both gates must be on (account.allow_service_splits AND the parent
 *   section's supports_splits) or the server returns 400.
 * - deleteServiceSplits: clears the set without changing AssignedUserID.
 *   Idempotent.
 */
export async function getServiceSplits(workOrderId, wosId) {
    try {
        const response = await this.client.get(`/work-orders/${workOrderId}/services/${wosId}/splits`);
        if (!response.success) {
            throw new Error(response.error || 'Failed to load service splits');
        }
        return (response.data?.splits ?? []);
    }
    catch (error) {
        console.error('[WorkOrdersService] Get service splits error:', error);
        throw error;
    }
}
export async function putServiceSplits(workOrderId, wosId, splits) {
    if (!splits || splits.length === 0) {
        throw new Error('putServiceSplits requires a non-empty splits array; use deleteServiceSplits to clear.');
    }
    try {
        const response = await this.client.put(`/work-orders/${workOrderId}/services/${wosId}/splits`, { splits });
        if (!response.success) {
            throw new Error(response.error || 'Failed to set service splits');
        }
        return (response.data?.splits ?? []);
    }
    catch (error) {
        console.error('[WorkOrdersService] Put service splits error:', error);
        throw error;
    }
}
export async function deleteServiceSplits(workOrderId, wosId) {
    try {
        const response = await this.client.delete(`/work-orders/${workOrderId}/services/${wosId}/splits`);
        if (!response.success) {
            throw new Error(response.error || 'Failed to clear service splits');
        }
    }
    catch (error) {
        console.error('[WorkOrdersService] Delete service splits error:', error);
        throw error;
    }
}
// Service operations
export async function addServiceToWorkOrder(workOrderId, serviceId, overrides, overridePin) {
    try {
        const body = { ...(overrides || {}) };
        if (overridePin) {
            body.override_pin = overridePin;
        }
        const response = await this.client.put(`/work-orders/${workOrderId}/services/${serviceId}`, body);
        if (!response.success) {
            // Preserve the parsed error body: guard-blocked responses carry a
            // structured `{declines, can_override}` envelope the web UI parses
            // (parseGuardDeclines) to offer a single manager-override retry.
            const err = new Error(response.error || 'Failed to add service');
            if (response.errorBody)
                err.errorBody = response.errorBody;
            throw err;
        }
    }
    catch (error) {
        console.error('[WorkOrdersService] Add service to work order error:', error);
        throw error;
    }
}
export async function removeServiceFromWorkOrder(workOrderId, serviceId, overridePin) {
    try {
        const path = `/work-orders/${workOrderId}/services/${serviceId}`;
        // Manager override PIN for guard-blocked removals travels in the DELETE
        // body (the backend body-parses it). Clients without body-carrying
        // DELETE support fall back to the plain form (no PIN — the server will
        // re-decline and the UI surfaces the declines again).
        const response = overridePin && this.client.deleteWithBody
            ? await this.client.deleteWithBody(path, { override_pin: overridePin })
            : await this.client.delete(path);
        if (!response.success) {
            // Keep the parsed error body so callers can detect the guard-decline
            // envelope ({declines, can_override}) and offer a manager override.
            const err = new Error(response.error || 'Failed to remove service');
            if (response.errorBody)
                err.errorBody = response.errorBody;
            throw err;
        }
    }
    catch (error) {
        console.error('[WorkOrdersService] Remove service from work order error:', error);
        throw error;
    }
}
/**
 * Update service status (complete or uncomplete)
 * For 'completed' status, returns ServiceCompletionResult with navigation hints
 * For 'pending' status, returns void
 */
export async function updateServiceStatus(workOrderId, wosId, status, 
// When the service has no assigned technician the backend rejects the
// completion with ASSIGNEE_REQUIRED. Pass assigneeUserId to bind a tech
// (a chosen tech, or the current user for "complete as myself") so credit
// reflects who performed the work.
assigneeUserId, 
// Manager override PIN for guard-blocked completions: when the server
// rejects the completion with a `{declines, can_override}` envelope, the
// UI collects one PIN and retries the same call with it.
overridePin) {
    try {
        const endpoint = status === 'completed' ? 'complete' : 'uncomplete';
        // require_assignee opts this (upgraded) client into the strict flow: an
        // unassigned service returns ASSIGNEE_REQUIRED so the UI can prompt,
        // instead of the legacy silent auto-assign-to-completer. The retry adds
        // assignee_user_id to bind the chosen tech (or self). Legacy/older app
        // builds omit the flag and keep the auto-assign behavior server-side.
        const body = status === 'completed'
            ? {
                require_assignee: true,
                ...(assigneeUserId ? { assignee_user_id: assigneeUserId } : {}),
                ...(overridePin ? { override_pin: overridePin } : {}),
            }
            : {};
        const response = await this.client.post(`/work-orders/${workOrderId}/services/${wosId}/${endpoint}`, body);
        if (!response.success) {
            // Propagate the server's structured code (e.g. ASSIGNEE_REQUIRED) so the
            // UI can prompt to pick a tech or complete as self.
            const code = response.errorBody?.code;
            if (code === 'COMPLETE_OTHERS_NOT_ALLOWED') {
                throw new Error('You do not have permission to complete work assigned to others');
            }
            // Keep the parsed error body so callers can detect the guard-decline
            // envelope ({declines, can_override}) and offer a manager override.
            const err = new Error(response.error || `Failed to ${endpoint} service`);
            if (code)
                err.code = code;
            if (response.errorBody)
                err.errorBody = response.errorBody;
            throw err;
        }
        // For completed status, return the ServiceCompletionResult if present
        if (status === 'completed' && response.data) {
            return response.data;
        }
    }
    catch (error) {
        console.error('[WorkOrdersService] Update service status error:', error);
        throw error;
    }
}
/**
 * Update service pricing fields (hours, prices, labor overrides)
 * Used for inline editing of service pricing in work order detail view
 */
export async function updateServicePricing(workOrderId, wosId, data, _templateServiceId // Deprecated parameter, kept for backwards compatibility
) {
    try {
        const response = await this.client.patch(`/work-orders/${workOrderId}/services/${wosId}`, data);
        if (!response.success) {
            // Preserve the parsed error body: the service.update hook point can
            // block this PATCH with the same `{declines, can_override}` envelope the
            // add path returns, and the editors offer a single manager-override
            // retry from it. Throwing a bare Error here would drop that envelope and
            // reduce an overridable block to an unexplained failure toast.
            const err = new Error(response.error || 'Failed to update service pricing');
            if (response.errorBody)
                err.errorBody = response.errorBody;
            throw err;
        }
    }
    catch (error) {
        console.error('[WorkOrdersService] Update service pricing error:', error);
        throw error;
    }
}
// Service approval methods
export async function approveService(workOrderId, serviceId) {
    try {
        const response = await this.client.post(`/work-orders/${workOrderId}/services/${serviceId}/approve`, {});
        if (!response.success) {
            throw new Error(response.error || 'Failed to approve service');
        }
    }
    catch (error) {
        console.error('[WorkOrdersService] Approve service error:', error);
        throw error;
    }
}
export async function denyService(workOrderId, serviceId) {
    try {
        const response = await this.client.post(`/work-orders/${workOrderId}/services/${serviceId}/deny`, {});
        if (!response.success) {
            throw new Error(response.error || 'Failed to deny service');
        }
    }
    catch (error) {
        console.error('[WorkOrdersService] Deny service error:', error);
        throw error;
    }
}
/**
 * Records the dealership's answer to a partner change request on a work order
 * (the connection has the dealership approve extra work). Works whatever state
 * the line is in; the partner is told the outcome.
 */
export async function recordDealershipDecision(workOrderId, changeRequestId, approve) {
    const response = await this.client.post(`/work-orders/${workOrderId}/partner-change-requests/${changeRequestId}/dealership-decision`, {
        approve,
    });
    if (!response.success) {
        throw new Error(response.error || "Failed to record the dealership's answer");
    }
}
export async function bulkApproveServices(workOrderId, approveIds, denyIds) {
    try {
        const response = await this.client.post(`/work-orders/${workOrderId}/services/bulk-approve`, {
            approve: approveIds,
            deny: denyIds,
        });
        if (!response.success) {
            throw new Error(response.error || 'Failed to bulk approve/deny services');
        }
    }
    catch (error) {
        console.error('[WorkOrdersService] Bulk approve services error:', error);
        throw error;
    }
}
/**
 * Approve a work order straight from the approval queue, in one click.
 *
 * POST /work-orders/{id}/approve REFUSES while any production service is still
 * `requested` ("N production service(s) are pending approval"), which is the
 * normal state of a freshly queued work order — so a bare approveWorkOrder()
 * call from the queue fails for exactly the orders the queue exists to clear.
 * Approving from the queue therefore means the same thing the detail view's
 * APPROVE ALL + APPROVE pair means: decide the outstanding services (approve
 * them), then approve the work order.
 *
 * Services that already carry a decision (approved / denied / auto_approved)
 * are left exactly as they are, so a reviewer who declined a line in the
 * detail view and came back to the queue does not have that decision undone.
 */
export async function approveWorkOrderFromQueue(workOrderId, queueServices = []) {
    const pendingIds = queueServices.filter((s) => s.approval_status === 'requested' && s.id).map((s) => s.id);
    if (pendingIds.length > 0) {
        await bulkApproveServices.call(this, workOrderId, pendingIds, []);
    }
    await approveWorkOrder.call(this, workOrderId);
}
export async function declineAllServices(workOrderId) {
    try {
        const response = await this.client.post(`/work-orders/${workOrderId}/decline-all`, {});
        if (!response.success) {
            throw new Error(response.error || 'Failed to decline all services');
        }
    }
    catch (error) {
        console.error('[WorkOrdersService] Decline all services error:', error);
        throw error;
    }
}
// Service hierarchy
export async function getServiceChildren(serviceId) {
    try {
        const response = await this.client.get(`/services/${serviceId}/children`);
        if (!response.success || !response.data) {
            return this.listFailure(response.error || 'Failed to get service children');
        }
        return extractItems(response.data, ['services', 'children']);
    }
    catch (error) {
        console.error('[WorkOrdersService] Get service children error:', error);
        return this.listFailure('Failed to get service children', error);
    }
}
export async function getServiceRelationships(serviceId) {
    try {
        const response = await this.client.get(`/services/${serviceId}/relationships`);
        if (!response.success || !response.data) {
            return this.listFailure(response.error || 'Failed to get service relationships');
        }
        return extractItems(response.data, ['relationships']);
    }
    catch (error) {
        console.error('[WorkOrdersService] Get service relationships error:', error);
        return this.listFailure('Failed to get service relationships', error);
    }
}
export async function getServiceHierarchy(serviceId) {
    try {
        const response = await this.client.get(`/services/${serviceId}/hierarchy`);
        if (!response.success || !response.data) {
            throw new Error(response.error || 'Failed to get service hierarchy');
        }
        return response.data;
    }
    catch (error) {
        console.error('[WorkOrdersService] Get service hierarchy error:', error);
        throw error;
    }
}
/**
 * Get work order services with optional filtering (flat list)
 */
export async function getWorkOrderServices(workOrderId, approvedOnly = false) {
    try {
        const params = approvedOnly ? { approved_only: 'true' } : undefined;
        const response = await this.client.get(`/work-orders/${workOrderId}/services`, params);
        if (!response.success || !response.data) {
            throw new Error(response.error || 'Failed to get work order services');
        }
        return extractItems(response.data, ['services']);
    }
    catch (error) {
        console.error('[WorkOrdersService] Get work order services error:', error);
        throw error;
    }
}
/**
 * Get all services for a work order with complete pricing information
 * GET /api/work-orders/{id}/services
 * Returns detailed service information including labor, parts, and calculated totals
 */
export async function getWorkOrderServicesDetailed(workOrderId, options) {
    try {
        const params = options?.approvedOnly ? { approved_only: 'true' } : undefined;
        const response = await this.client.get(`/work-orders/${workOrderId}/services`, params);
        if (!response.success || !response.data) {
            throw new Error(response.error || 'Failed to get work order services');
        }
        const data = response.data;
        const items = (data?.items ?? data?.services ?? data?.data ?? data) || [];
        return {
            items,
            total: data?.total ?? items.length,
            count: data?.count ?? items.length,
        };
    }
    catch (error) {
        console.error('[WorkOrdersService] Get work order services detailed error:', error);
        throw error;
    }
}
